import React, { useState } from 'react';
import { GraduationCap, ChevronRight, Search, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { entranceExamCategories } from '../data/enteranceExamData';

interface EntranceExam {
  id: string;
  title: string;
  lastDateToApply?: string;
}

const TestSeriesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Set the icon property for the imported category
  const examCategories = entranceExamCategories.map(cat => ({
    ...cat,
    icon: GraduationCap
  }));

  const filteredCategories = examCategories.filter(category => 
    !selectedCategory || category.title === selectedCategory
  );

  const renderExamCard = (exam: EntranceExam) => {
    return <EntranceExamCard exam={exam} />;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-600 to-green-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Agricultural Test Series
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto mb-8">
              Comprehensive preparation material for all major agricultural examinations
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for test series..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-[24px] bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <div className="bg-white dark:bg-gray-800 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 py-4 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                !selectedCategory 
                  ? 'bg-green-800 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              aria-label="All Categories"
            >
              All Categories
            </button>
            {examCategories.map((category) => (
              <button
                key={category.title}
                onClick={() => setSelectedCategory(category.title)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === category.title
                    ? 'bg-green-800 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                aria-label={category.title}
              >
                {category.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Test Series Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8">
            {filteredCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-[24px] overflow-hidden shadow-lg">
                  {/* Category Header */}
                  <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                      <div className="h-12 w-12 bg-green-100 dark:bg-green-900/50 rounded-[24px] flex items-center justify-center mr-4">
                        <Icon className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {category.title}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Exams Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                    {category.exams.map((exam) => (
                      <div key={exam.id}>
                        {renderExamCard(exam as EntranceExam)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

// Exam Card Components
const EntranceExamCard: React.FC<{ exam: EntranceExam }> = ({ exam }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
      <div className="p-5">
        <div className="mb-4">
          <button
            type="button"
            onClick={() => navigate(`/test-series/${exam.id}`)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-green-600 text-green-700 dark:text-green-400 bg-white dark:bg-gray-800 font-semibold shadow-sm transition-all hover:bg-green-50 dark:hover:bg-green-900/30 hover:border-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
            aria-label={exam.title}
          >
            <span>{exam.title}</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        {exam.lastDateToApply && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-[24px]">
            <div className="flex items-center text-red-700 dark:text-red-400">
              <Calendar className="h-4 w-4 mr-2" />
              <span className="text-sm">Last Date: {exam.lastDateToApply}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestSeriesPage; 